// General Settings Page - Organization profile settings
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Business,
  Save,
  Language,
  Schedule,
  Email,
  Link as LinkIcon,
  Visibility,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useToast } from '@/contexts/ToastContext';
import { usePermissions } from '@/hooks/usePermissions';
import { organizationsService } from '@/services/collections/organizations';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/services/firebase/config';

// Timezones (common ones)
const TIMEZONES = [
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
  { value: 'America/New_York', label: 'America/New_York (EST)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST)' },
  { value: 'America/Denver', label: 'America/Denver (MST)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST)' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'zh', label: 'Chinese' },
];

const settingsSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters'),
  slug: z.string().min(2, 'Slug must be at least 2 characters').regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  supportEmail: z.string().email('Invalid email address'),
  timezone: z.string().min(1, 'Please select a timezone'),
  defaultLanguage: z.string().min(1, 'Please select a language'),
  autoReplyEnabled: z.boolean(),
  autoReplyMessage: z.string().optional(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;

export const GeneralSettingsPage = () => {
  const navigate = useNavigate();
  const { organization, refreshOrganization, project, refreshProjects } = useOrganization();
  const { showSuccess, showError } = useToast();
  const { can, plan } = usePermissions();
  const [saving, setSaving] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [featureToggles, setFeatureToggles] = useState({
    featureRequestsEnabled: true,
    knowledgeBaseEnabled: true,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      name: '',
      slug: '',
      supportEmail: '',
      timezone: '',
      defaultLanguage: 'en',
      autoReplyEnabled: false,
      autoReplyMessage: '',
    },
  });

  const autoReplyEnabled = watch('autoReplyEnabled');

  // Load project feature toggles
  useEffect(() => {
    if (project) {
      setFeatureToggles({
        featureRequestsEnabled: project.features?.featureRequestsEnabled ?? true,
        knowledgeBaseEnabled: project.features?.knowledgeBaseEnabled ?? true,
      });
    }
  }, [project]);

  // Save feature toggles
  const handleSaveFeatures = async () => {
    if (!project) return;

    setSavingFeatures(true);
    try {
      await organizationsService.updateProject(organization!.id, project.id, {
        features: {
          ...project.features,
          featureRequestsEnabled: featureToggles.featureRequestsEnabled,
          knowledgeBaseEnabled: featureToggles.knowledgeBaseEnabled,
        },
      } as any);
      await refreshProjects();
      showSuccess('Portal feature settings saved');
    } catch (err) {
      console.error('Error saving feature settings:', err);
      showError('Failed to save feature settings');
    } finally {
      setSavingFeatures(false);
    }
  };

  // Load organization data into form
  useEffect(() => {
    if (organization) {
      reset({
        name: organization.name,
        slug: organization.slug,
        supportEmail: organization.settings?.supportEmail || '',
        timezone: organization.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        defaultLanguage: organization.settings?.defaultLanguage || 'en',
        autoReplyEnabled: organization.settings?.autoReplyEnabled || false,
        autoReplyMessage: organization.settings?.autoReplyMessage || '',
      });
    }
  }, [organization, reset]);

  const onSubmit = async (data: SettingsFormData) => {
    if (!organization) return;

    setSaving(true);
    try {
      await updateDoc(doc(db, 'organizations', organization.id), {
        name: data.name,
        slug: data.slug,
        'settings.supportEmail': data.supportEmail,
        'settings.timezone': data.timezone,
        'settings.defaultLanguage': data.defaultLanguage,
        'settings.autoReplyEnabled': data.autoReplyEnabled,
        'settings.autoReplyMessage': data.autoReplyMessage || '',
        updatedAt: serverTimestamp(),
      });

      await refreshOrganization();
      showSuccess('Settings saved successfully!');
    } catch (err) {
      console.error('Error saving settings:', err);
      showError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!organization) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const canEdit = can('org:settings');

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          General Settings
        </Typography>
        <Typography color="text.secondary">
          Manage your organization profile and preferences
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Organization Profile */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Business sx={{ color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Organization Profile
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Basic information about your organization
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      {...register('name')}
                      label="Organization Name"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      {...register('slug')}
                      label="URL Slug"
                      fullWidth
                      error={!!errors.slug}
                      helperText={errors.slug?.message || `Your portal URL: supportspace.web.app/p/${watch('slug') || 'your-org'}`}
                      disabled={!canEdit}
                      InputProps={{
                        startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      {...register('supportEmail')}
                      label="Support Email"
                      fullWidth
                      error={!!errors.supportEmail}
                      helperText={errors.supportEmail?.message || 'Email address shown to customers for support inquiries'}
                      disabled={!canEdit}
                      InputProps={{
                        startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Plan Info Sidebar */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  CURRENT PLAN
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                    {plan}
                  </Typography>
                  <Chip
                    label={plan === 'free' ? 'Free' : 'Paid'}
                    size="small"
                    sx={{
                      background: plan === 'free' ? '#e2e8f0' : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      color: plan === 'free' ? 'text.secondary' : 'white',
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {plan === 'free' && 'Upgrade to unlock more features like custom branding and analytics.'}
                  {plan === 'pro' && 'You have access to custom branding and priority support.'}
                  {plan === 'business' && 'Full access to all features including custom domains.'}
                  {plan === 'enterprise' && 'Enterprise plan with dedicated support.'}
                </Typography>
                {plan === 'free' && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate('/', { state: { scrollTo: 'pricing' } })}
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    }}
                  >
                    Upgrade Plan
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  USAGE
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Projects</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {organization.usage?.projectCount || 0} / {organization.limits?.projects === -1 ? '∞' : organization.limits?.projects}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Team Members</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {organization.usage?.seatCount || 0} / {organization.limits?.seats === -1 ? '∞' : organization.limits?.seats}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">KB Articles</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {organization.usage?.articleCount || 0} / {organization.limits?.knowledgeBaseArticles === -1 ? '∞' : organization.limits?.knowledgeBaseArticles}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Localization Settings */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Language sx={{ color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Localization
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Language and timezone preferences
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="defaultLanguage"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.defaultLanguage}>
                          <InputLabel>Default Language</InputLabel>
                          <Select {...field} label="Default Language" disabled={!canEdit}>
                            {LANGUAGES.map((lang) => (
                              <MenuItem key={lang.value} value={lang.value}>
                                {lang.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="timezone"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.timezone}>
                          <InputLabel>Timezone</InputLabel>
                          <Select
                            {...field}
                            label="Timezone"
                            disabled={!canEdit}
                            startAdornment={<Schedule sx={{ mr: 1, color: 'text.secondary' }} />}
                          >
                            {TIMEZONES.map((tz) => (
                              <MenuItem key={tz.value} value={tz.value}>
                                {tz.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Auto Reply Settings */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Email sx={{ color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Auto-Reply
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatic responses for new tickets
                    </Typography>
                  </Box>
                </Box>

                <Controller
                  name="autoReplyEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value}
                          disabled={!canEdit}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#3b82f6',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#3b82f6',
                            },
                          }}
                        />
                      }
                      label="Enable auto-reply for new tickets"
                      sx={{ mb: 2 }}
                    />
                  )}
                />

                {autoReplyEnabled && (
                  <TextField
                    {...register('autoReplyMessage')}
                    label="Auto-Reply Message"
                    fullWidth
                    multiline
                    rows={4}
                    disabled={!canEdit}
                    placeholder="Thank you for contacting us. We've received your message and will get back to you within 24 hours."
                    helperText="This message will be sent automatically when a new ticket is created"
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Portal Feature Toggles */}
          {project && (
            <Grid size={{ xs: 12, lg: 8 }}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Visibility sx={{ color: 'white' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        Portal Features
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Control which features are visible on the public support portal ({project.name})
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={featureToggles.knowledgeBaseEnabled}
                          onChange={(e) => setFeatureToggles((prev) => ({ ...prev, knowledgeBaseEnabled: e.target.checked }))}
                          disabled={!canEdit}
                        />
                      }
                      label="Knowledge Base - Allow customers to browse help articles"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={featureToggles.featureRequestsEnabled}
                          onChange={(e) => setFeatureToggles((prev) => ({ ...prev, featureRequestsEnabled: e.target.checked }))}
                          disabled={!canEdit}
                        />
                      }
                      label="Feature Voting & Ideas - Allow customers to submit and vote on feature ideas"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                      Disabling Feature Voting also hides the Public Roadmap tab from the portal.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSaveFeatures}
                      disabled={!canEdit || savingFeatures}
                      startIcon={savingFeatures ? <CircularProgress size={16} /> : <Save />}
                    >
                      {savingFeatures ? 'Saving...' : 'Save Portal Features'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Save Button */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              {!canEdit && (
                <Alert severity="info" sx={{ flex: 1 }}>
                  You don't have permission to edit settings. Contact your organization admin.
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={!canEdit || saving || !isDirty}
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                sx={{
                  px: 4,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
